import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  InputNumber,
  Form,
  Typography,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Col,
  Row,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { storeArrival, updateArrival } from '@app/api/import.api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;

interface ShipmentArrivalsEditProps {
  import_id: string | null;
  importLinesVarieties: any[];
  arrivals: any[];
  status: string | null;
  editArrival: any[];
  ArrivalProducts: any[];
  AllowedWeightage: number;
  ImportedWeightage: number;
}

const ShipmentArrivalsEdit: React.FC<ShipmentArrivalsEditProps> = ({
  import_id,
  importLinesVarieties,
  arrivals,
  editArrival,
  ArrivalProducts,
  AllowedWeightage,
  ImportedWeightage,
}) => {
  const [form] = Form.useForm();
  const [importLines, setImportLines] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editingArrivalId, setEditingArrivalId] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [arrivalDate, setArrivalDate] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [newRow, setNewRow] = useState<any>({ selectedImportLineId: undefined, weight: null });
  const [currentImportedWeightage, setCurrentImportedWeightage] = useState<number>(Number(ImportedWeightage));

  useEffect(() => {
    if (importLinesVarieties && arrivals) {
      const formattedLines = importLinesVarieties
        .filter((line: any, index: number, self: any[]) => index === self.findIndex((l) => l.id === line.id))
        .map((line: any) => ({
          key: line.id,
          import_line_id: line.id,
          product_id: line.product_id,
          label: `${line.product.code} - ${line.product.name}`,
          productVarieties: line.import_line_varieties || [],
          weight: null,
        }));
      setImportLines(formattedLines);

      const existingArrivals = arrivals.flatMap((arrival: any) =>
        (arrival.arrival_lines || []).map((line: any, i: number) => ({
          key: `existing-${arrival.id}-${i}`,
          arrival_id: arrival.id,
          import_line_id: line.import_line_id,
          product_id: line.product_id,
          selectedProductVariety: line.product_variety || 'Unknown',
          weight: line.weight || '0',
          date: arrival.date ? dayjs(arrival.date).format('YYYY-MM-DD') : 'N/A',
          description: arrival.description || 'No description',
          isNew: false,
        })),
      );
      setTableData(existingArrivals);
      console.log('Existing Arrivals:', existingArrivals);
    }
    console.log('importLinesVarieties raw:', importLinesVarieties);
    console.log('ArrivalProducts:', ArrivalProducts);
  }, [importLinesVarieties, arrivals]);

  const isEditing = (record: any) => record.key === editingKey;

  const edit = (record: any) => {
    setEditingKey(record.key);
    setEditingRowKey(record.key);
    setEditingArrivalId(record.arrival_id);
    setDescription(record.description || '');
    setArrivalDate(record.date ? dayjs(record.date) : null);
    setNewRow({
      selectedImportLineId: record.import_line_id, // <-- changed
      weight: record.weight,
    });
  };

  const cancel = () => {
    setEditingKey(null);
    setEditingRowKey(null);
    setEditingArrivalId(null);
    setDescription('');
    setArrivalDate(null);
    setNewRow({ selectedImportLineId: undefined, weight: null });
  };

  const handleAddOrUpdate = async () => {
    if (newRow.selectedImportLineId == null || newRow.weight == null) {
      return message.warning('Please fill Product and Weight.');
    }

    const commonData = {
      import_line_id: newRow.selectedImportLineId,
      weight: newRow.weight,
      date: arrivalDate ? dayjs(arrivalDate).format('YYYY-MM-DD') : null,
      description,
    };

    if (editingArrivalId) {
      const newData = tableData.map((item) => (item.key === editingRowKey ? { ...item, ...commonData } : item));

      const payload = {
        description: commonData.description,
        date: commonData.date === null ? undefined : commonData.date,
        arrival_lines: [
          {
            import_line_id: commonData.import_line_id,
            weight: commonData.weight,
          },
        ],
      };

      try {
        setLoading(true);
        await updateArrival(Number(import_id), editingArrivalId, payload);
        message.success('Arrival updated!');

        const oldRow = tableData.find((item) => item.key === editingRowKey);
        const oldWeight = oldRow ? Number(oldRow.weight) : 0;
        const weightDiff = Number(commonData.weight) - oldWeight;
        setCurrentImportedWeightage((prev) => prev + weightDiff);

        setTableData(newData);
        cancel();
      } catch {
        message.error('Error updating arrival.');
      } finally {
        setLoading(false);
      }
      return;
    }

    const newData = {
      key: `new-${Date.now()}`,
      ...commonData,
      isNew: true,
    };

    setTableData([newData, ...tableData]);
    setNewRow({ selectedImportLineId: undefined, weight: null });
  };

  const handleDeleteRow = (key: string) => {
    setTableData(tableData.filter((row) => row.key !== key));
  };

  const handleSaveArrival = async () => {
    const newRows = tableData.filter((row) => row.isNew);
    if (!description || !arrivalDate || newRows.length === 0) {
      return message.warning('Fill description, date, and add at least one item.');
    }

    const valid = newRows.every((row) => row.weight != null && row.import_line_id);
    if (!valid) return message.warning('Check all Line IDs and weights.');

    const payload = {
      description,
      date: dayjs(arrivalDate).format('YYYY-MM-DD'),
      arrival_lines: newRows.map((row) => ({
        import_line_id: row.import_line_id,
        weight: row.weight,
      })),
    };

    try {
      setLoading(true);
      await storeArrival(Number(import_id), payload);
      message.success('Arrival saved!');
      const addedWeight = newRows.reduce((sum: number, row: any) => sum + Number(row.weight), 0);
      setCurrentImportedWeightage((prev) => prev + addedWeight);
      setDescription('');
      setArrivalDate(null);
      setTableData((prev) => prev.map((row) => (row.isNew ? { ...row, isNew: false } : row)));
    } catch {
      message.error('Error saving arrival.');
    } finally {
      setLoading(false);
    }
  };

  const remainingWeight = Number(AllowedWeightage) - currentImportedWeightage;

  const mergedColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      render: (val: any) => {
        return val ? dayjs(val).format('YYYY-MM-DD') : '-';
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (val: any) => val || '-',
    },
    {
      title: 'Product',
      dataIndex: 'import_line_id',
      render: (val: any) => {
        const line = importLines.find((l) => l.import_line_id === val);
        const product = line ? ArrivalProducts.find((p) => p.id === line.product_id) : null;
        return product ? product.label : '-';
      },
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight',
      render: (val: any) => <span>{val} kg</span>,
    },

    ...(editArrival
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: any) => (
              <>
                <Button icon={<EditOutlined />} onClick={() => edit(record)} size="small" />
                {record.isNew && (
                  <Popconfirm title="Delete this row?" onConfirm={() => handleDeleteRow(record.key)}>
                    <Button icon={<DeleteOutlined />} size="small" danger style={{ marginLeft: 8 }} />
                  </Popconfirm>
                )}
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      extra={
        <span style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>
          Imported Weightage: {currentImportedWeightage}/{AllowedWeightage} KG
        </span>
      }
    >
      {editArrival && (
        <Form layout="vertical" form={form} component={false}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Date" required>
                <DatePicker value={arrivalDate} onChange={(val) => setArrivalDate(val)} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Product" required>
                <Select
                  value={newRow.selectedImportLineId}
                  onChange={(val) => setNewRow((prev: any) => ({ ...prev, selectedImportLineId: val }))}
                  placeholder="Select Product"
                  style={{ width: '100%' }}
                  dropdownStyle={{ minWidth: '400px' }}
                >
                  {importLines.map((line) => (
                    <Select.Option key={line.import_line_id} value={line.import_line_id}>
                      {line.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Weight (kg)" required>
                <InputNumber
                  value={newRow.weight}
                  min={0}
                  onChange={(val) => setNewRow((prev: any) => ({ ...prev, weight: val }))}
                  style={{ width: '100%' }}
                  status={newRow.weight != null && newRow.weight > remainingWeight ? 'warning' : ''}
                />
                {newRow.weight != null && newRow.weight > remainingWeight && (
                  <span style={{ color: 'orange', fontSize: '12px' }}>
                    ⚠️ Exceeds remaining allowed weightage ({remainingWeight} kg left)
                  </span>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Description" required>
                <TextArea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <Col>
              <Button type="primary" onClick={handleAddOrUpdate}>
                {editingRowKey ? 'Update' : 'Add'}
              </Button>
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* ✅ Show only if already exceeded before any new input */}
              {currentImportedWeightage > Number(AllowedWeightage) && (
                <span style={{ color: 'red', fontSize: '12px', fontWeight: 'bold' }}>
                  ⚠️ Allowed weightage already exceeded!
                </span>
              )}
              <Button type="primary" onClick={handleSaveArrival} loading={loading}>
                Save Arrival
              </Button>
            </Col>
          </Row>
          <br />
        </Form>
      )}
      <Table dataSource={tableData} columns={mergedColumns} rowKey="key" pagination={false} bordered />
    </Card>
  );
};

export default ShipmentArrivalsEdit;
